import React, { useEffect } from "react";
import moment from "moment";
import { Modal, Button, Form, Input, InputNumber, Select } from "antd";
import Wrapper from "./style";
import { customBaseUrl, httpPost } from "../../../store/http";
import { hideLoader, showLoader } from "../../../utils/loader";
import { NotificationManager } from "react-notifications";

const FundGLModal = ({ showModal, hideModal, onSuccess, selectedAccount, allAccounts }) => {
    const [form] = Form.useForm();
    const adminEmail = localStorage.getItem('email');
    const targetAccountCode = Form.useWatch('targetGlAccountCode', form);

    const isExternalFund = targetAccountCode === '30030001';

    useEffect(() => {
        if (showModal && selectedAccount) {
            form.setFieldsValue({ targetGlAccountCode: selectedAccount.accountCode });
        }
    }, [showModal, selectedAccount, form]);

    const handleFund = async (values) => {
        showLoader();

        let payload;
        let endpoint;

        if (isExternalFund) {
            // Customize payload for Owner Equity (30030001)
            payload = {
                amount: values.amount,
                narration: values.narration,
                transactionReference: `EXT-FUND-${moment().format('YYYYMMDD')}-${Date.now().toString().slice(-3)}`,
                createdBy: adminEmail || 'ADMIN',
                accountCode: '30030001'
            };
            endpoint = `${customBaseUrl.tranv2}/api/v1/cba/gl/external-fund`;
        } else {
            // Standard funding payload
            payload = {
                ...values,
                transactionReference: `FUND-${Date.now()}`,
                createdBy: adminEmail || 'admin'
            };
            endpoint = `${customBaseUrl.tranv2}/api/v1/cba/gl/fund`;
        }

        try {
            const res = await httpPost(endpoint, payload, false, null, false);
            if (res?.success) {
                NotificationManager.success('GL Account funded successfully!');
                hideModal();
                form.resetFields();
                if (onSuccess) onSuccess();
            } else {
                NotificationManager.error(res?.message || 'Failed to fund GL account.');
            }
        } catch (error) {
            console.error("Funding error:", error);
            NotificationManager.error('An error occurred while funding GL account.');
        } finally {
            hideLoader();
        }
    };

    return (
        <Modal
            visible={showModal}
            title={isExternalFund ? "External Funding: Owner Equity" : "Fund GL Account"}
            onCancel={hideModal}
            footer={null}
            centered
            width={500}
        >
            <Wrapper>
                <div className="title" style={{ marginBottom: 24, fontSize: 18, fontWeight: 700 }}>
                    {isExternalFund ? "External Funding (Owner Equity)" : "Fund GL Account"}
                </div>
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleFund}
                >
                    <Form.Item
                        label="Target GL Account Code"
                        name="targetGlAccountCode"
                        rules={[{ required: true, message: 'Target account is required' }]}
                    >
                        <Select
                            showSearch
                            placeholder="Select target account"
                            filterOption={(input, option) => {
                                const children = Array.isArray(option.children) ? option.children.join("") : option.children;
                                return (children || "").toString().toLowerCase().indexOf(input.toLowerCase()) >= 0;
                            }}
                        >
                            {allAccounts.map(acc => (
                                <Select.Option key={acc.accountCode} value={acc.accountCode}>
                                    {acc.accountName} ({acc.accountCode})
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    {!isExternalFund && (
                        <Form.Item
                            label="Source GL Account Code"
                            name="sourceGlAccountCode"
                            rules={[{ required: true, message: 'Source account is required' }]}
                        >
                            <Select
                                showSearch
                                placeholder="Select source account"
                                filterOption={(input, option) => {
                                    const children = Array.isArray(option.children) ? option.children.join("") : option.children;
                                    return (children || "").toString().toLowerCase().indexOf(input.toLowerCase()) >= 0;
                                }}
                            >
                                {allAccounts.map(acc => (
                                    <Select.Option key={acc.accountCode} value={acc.accountCode}>
                                        {acc.accountName} ({acc.accountCode})
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}

                    <Form.Item
                        label="Amount"
                        name="amount"
                        rules={[{ required: true, message: 'Amount is required' }]}
                    >
                        <InputNumber
                            style={{ width: '100%', height: 40 }}
                            formatter={value => `₦ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={value => value.replace(/₦\s?|(,*)/g, '')}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Narration"
                        name="narration"
                        rules={[{ required: true, message: 'Narration is required' }]}
                    >
                        <Input.TextArea rows={3} placeholder={isExternalFund ? "e.g. Zenith bank balance - March 2026" : "Enter narration"} />
                    </Form.Item>

                    <Form.Item style={{ marginTop: 24, marginBottom: 0 }}>
                        <Button
                            type="primary"
                            htmlType="submit"
                            block
                            style={{
                                height: 48,
                                background: '#FF4B01',
                                border: 'none',
                                fontWeight: 600,
                                borderRadius: 8
                            }}
                        >
                            Confirm {isExternalFund ? "External Funding" : "Funding"}
                        </Button>
                    </Form.Item>
                </Form>
            </Wrapper>
        </Modal>
    );
};

export default FundGLModal;
